import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {JobType} from '../../api/types/aiReview.type';
import {ToastType} from '../../shared/constants';
import {ISelectItem} from '../../shared/types';

export type SourceSelectionMode = 'SAVEDSEARCH' | 'TAG' | 'FOLDER';

export interface SourceSelectionFormData {
  sourceSelection: number;
  sourceSelectionMode: SourceSelectionMode;
  sourceSelectionName?: string;
  totalDocs?: number | null;
  groundTruthTagId?: number | null;
}

interface ToastState {
  open: boolean;
  title: string;
  message: string;
  status: ToastType[];
}

interface EditModeState {
  caseContext: boolean;
  relevanceCriteria: boolean;
  issueCode: boolean;
}

export interface JobDetailData {
  jobName: string;
  description: string;
  selectedGroup: ISelectItem | null;
}

export interface JobDetailFormData {
  jobId?: number;
  jobDetail: JobDetailData;
  caseContext: CaseContext;
  relevanceCriteria: RelevanceCriteria;
  issueCoding: IssueCoding;
  sourceSelection: number;
  sourceSelectionMode: SourceSelectionMode;
  sourceSelectionName?: string;
  totalDocs?: number | null;
  groundTruthTagId?: number | null;
  reviewProtocolVersionID?: number | null;
  parentProtocolVersionID?: number | null;
  parentProtocolID?: number | null;
  isProtocolModified: boolean;
  parentReviewJobID?: number | null;
  isManual?: boolean;
}

export type ProtocolStatus = 'draft' | 'approved';

export interface PartyRole {
  party: string;
  role: string;
  party_description: string;
}

export interface CaseContext {
  executive_summary: string;
  parties_and_roles: PartyRole[];
  claims: string[];
  defenses: string[];
  disputed_facts: string[];
  key_legal_and_factual_issues: string[];
  primary_date_range: string;
  important_subperiods: string[];
  custodians: string[];
  departments: string[];
  systems_and_repositories: string[];
  responsiveness_standard_definition: string;
  responsive_examples: string[];
  non_responsive_examples: string[];
  relevancy_keywords: string[];
  relevance_themes: string[];
  behavioral_indicators: string[];
  decision_making_indicators: string[];
  explicit_exclusions: string[];
  reviewer_instructions: string[];
  borderline_scenarios: string[];
  ambiguities_and_known_limits: string[];
}

export interface ExclusionGate {
  id: string;
  other: string;
  hard_rule: string;
  tag: string;
}

export interface PrimaryDecisionQuestion {
  id: string;
  question_text: string;
  rationale: string;
}

export interface SecondarySupportingQuestion {
  id: string;
  question_text: string;
  purpose: string;
}

export interface NegativeExclusionQuestion {
  id: string;
  question_text: string;
  purpose: string;
}

export interface EscalationTrigger {
  id: string;
  question_text: string;
  purpose: string;
}

export interface RelevanceCriteria {
  master_relevance_standard: string;
  exclusion_gates: ExclusionGate[];
  primary_decision_questions: PrimaryDecisionQuestion[];
  secondary_supporting_questions: SecondarySupportingQuestion[];
  negative_exclusion_questions: NegativeExclusionQuestion[];
  escalation_triggers: EscalationTrigger[];
}

export interface TopLevelIssueCategory {
  category_id: string;
  category_label: string;
  category_description: string;
}

export type IssueCodePriority = 'Core' | 'Important' | 'Peripheral';

export interface IssueCodeDefinition {
  code_id: string;
  code_name: string;
  category: string; // references TopLevelIssueCategory.category_id
  priority: string;
  definition: string;
  include_if_any_apply: string[];
  exclude_if_any_apply: string[];
  responsive_examples: string[];
  non_responsive_examples: string[];
}

export interface IssueCoding {
  top_level_issue_categories: TopLevelIssueCategory[];
  issue_codes: IssueCodeDefinition[];
  cross_issue_overlap_rules: string[];
  edge_case_handling: string[];
}

export type ProtocolSectionKey =
  | 'caseContext'
  | 'relevanceCriteria'
  | 'issueCoding';

export interface ProtocolSectionMap {
  caseContext: CaseContext;
  relevanceCriteria: RelevanceCriteria;
  issueCoding: IssueCoding;
}

export interface ProtocolFormData {
  protocolId?: number;
  protocolVersionId?: number;
  protocolName: string;
  protocolVersionName?: string;
  caseContext: CaseContext;
  relevanceCriteria: RelevanceCriteria;
  issueCoding: IssueCoding;
  status: ProtocolStatus;
  /** Present for document-based create/edit when a protocol file was uploaded. */
  fileDetails?: {
    fileName: string;
    artifactId: number | null;
  };
}

export interface ProtocolVersionSummary {
  versionId: number;
  name: string;
  status: ProtocolStatus;
}

interface ProtocolVersionsState {
  protocolId: number | null;
  versions: ProtocolVersionSummary[];
}

export const emptyCaseContext = (): CaseContext => ({
  executive_summary: '',
  parties_and_roles: [],
  claims: [],
  defenses: [],
  disputed_facts: [],
  key_legal_and_factual_issues: [],
  primary_date_range: '',
  important_subperiods: [],
  custodians: [],
  departments: [],
  systems_and_repositories: [],
  responsiveness_standard_definition: '',
  responsive_examples: [],
  non_responsive_examples: [],
  relevancy_keywords: [],
  relevance_themes: [],
  behavioral_indicators: [],
  decision_making_indicators: [],
  explicit_exclusions: [],
  reviewer_instructions: [],
  borderline_scenarios: [],
  ambiguities_and_known_limits: [],
});

export const emptyRelevanceCriteria = (): RelevanceCriteria => ({
  master_relevance_standard: '',
  exclusion_gates: [],
  primary_decision_questions: [],
  secondary_supporting_questions: [],
  negative_exclusion_questions: [],
  escalation_triggers: [],
});

export const emptyIssueCoding = (): IssueCoding => ({
  top_level_issue_categories: [
    {
      category_id: '',
      category_label: '',
      category_description: '',
    },
  ],
  issue_codes: [
    {
      code_id: '',
      code_name: '',
      category: '',
      priority: '',
      definition: '',
      include_if_any_apply: [''],
      exclude_if_any_apply: [''],
      responsive_examples: [''],
      non_responsive_examples: [''],
    },
  ],
  cross_issue_overlap_rules: [''],
  edge_case_handling: [''],
});

const emptyProtocol = (): ProtocolFormData => ({
  protocolName: '',
  caseContext: emptyCaseContext(),
  relevanceCriteria: emptyRelevanceCriteria(),
  issueCoding: emptyIssueCoding(),
  status: 'draft',
});

const emptyProtocolVersions = (): ProtocolVersionsState => ({
  protocolId: null,
  versions: [],
});

// -----------------------------------------------------------------------------
// Initial state
// -----------------------------------------------------------------------------

const formDataInitialState: JobDetailFormData = {
  jobDetail: {jobName: '', description: '', selectedGroup: null},
  caseContext: emptyCaseContext(),
  relevanceCriteria: emptyRelevanceCriteria(),
  issueCoding: emptyIssueCoding(),
  sourceSelectionMode: 'SAVEDSEARCH',
  sourceSelection: 0,
  sourceSelectionName: '',
  totalDocs: null,
  groundTruthTagId: null,
  reviewProtocolVersionID: undefined,
  parentProtocolVersionID: undefined,
  parentProtocolID: undefined,
  isProtocolModified: false,
  parentReviewJobID: undefined,
  isManual: true,
};

const toastInitialState: ToastState = {
  open: false,
  title: '',
  message: '',
  status: [],
};

const editModeInitialState: EditModeState = {
  caseContext: false,
  relevanceCriteria: false,
  issueCode: false,
};

interface AiReviewJobType {
  jobType: JobType;
  formData: JobDetailFormData;
  initialFormData: JobDetailFormData; // baseline for the job's unsaved-changes check
  cloneJobName: string;
  protocol: ProtocolFormData; // working copy — Create/Edit/Get Protocol all use this
  initialProtocol: ProtocolFormData; // last-saved/last-loaded snapshot, for dirty checks
  protocolVersionBaseline: ProtocolFormData | null; // canonical selected version, for Reset
  /** Local draft snapshot until create/draft API is wired */
  lastSavedDraft: ProtocolFormData | null;
  protocolVersions: ProtocolVersionsState; // from "Get Protocol All Versions"
  toast: ToastState;
  editMode: EditModeState;
  defineCriteria: string;
}

const initialState: AiReviewJobType = {
  jobType: 'RELEVANCY',
  formData: formDataInitialState,
  initialFormData: formDataInitialState,
  cloneJobName: '',
  protocol: emptyProtocol(),
  initialProtocol: emptyProtocol(),
  protocolVersionBaseline: null,
  lastSavedDraft: null,
  protocolVersions: emptyProtocolVersions(),
  toast: toastInitialState,
  editMode: editModeInitialState,
  defineCriteria: 'manual',
};

export const AiReviewSliceV2 = createSlice({
  name: 'aiReviewV2',
  initialState,
  reducers: {
    setJobType(state, action: PayloadAction<JobType>) {
      state.jobType = action.payload;
    },

    setFormData(state, action: PayloadAction<Partial<JobDetailFormData>>) {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },

    updateJobSection<K extends ProtocolSectionKey>(
      state: AiReviewJobType,
      action: PayloadAction<{section: K; data: Partial<ProtocolSectionMap[K]>}>
    ) {
      const {section, data} = action.payload;
      switch (section) {
        case 'caseContext':
          state.formData.caseContext = {
            ...state.formData.caseContext,
            ...(data as Partial<CaseContext>),
          };
          break;
        case 'relevanceCriteria':
          state.formData.relevanceCriteria = {
            ...state.formData.relevanceCriteria,
            ...(data as Partial<RelevanceCriteria>),
          };
          break;
        case 'issueCoding':
          state.formData.issueCoding = {
            ...state.formData.issueCoding,
            ...(data as Partial<IssueCoding>),
          };
          break;
      }
    },

    resetFormData(state) {
      state.formData = formDataInitialState;
      state.initialFormData = formDataInitialState;
    },

    /** Seeds empty protocol for a new job create wizard (shared protocol tab components). */
    initializeJobCreateProtocol(state) {
      const next = emptyProtocol();
      state.protocol = next;
      state.initialProtocol = next;
      state.protocolVersionBaseline = null;
    },

    /** Copies protocol tab edits into formData before save/submit. */
    syncProtocolToFormData(state) {
      state.formData = {
        ...state.formData,
        caseContext: state.protocol.caseContext,
        relevanceCriteria: state.protocol.relevanceCriteria,
        issueCoding: state.protocol.issueCoding,
      };
    },
    setInitialFormData(state, action: PayloadAction<JobDetailFormData>) {
      state.initialFormData = action.payload;
    },
    setCloneJobName(state, action: PayloadAction<string>) {
      state.cloneJobName = action.payload;
    },

    // Protocol reducers — Create Protocol, Edit Protocol, Get Protocol

    setProtocolName(state, action: PayloadAction<string>) {
      state.protocol.protocolName = action.payload;
      // Name was persisted via API — align baseline so exit/dirty guards stay clean.
      state.initialProtocol.protocolName = action.payload;
    },

    // Seeds both working + baseline protocol so create starts clean (not dirty).
    initializeNewProtocol(state, action: PayloadAction<string>) {
      const next = {
        ...emptyProtocol(),
        protocolName: action.payload.trim(),
      };
      state.protocol = next;
      state.initialProtocol = next;
      state.protocolVersionBaseline = null;
      state.protocolVersions = emptyProtocolVersions();
    },

    updateProtocolSection<K extends ProtocolSectionKey>(
      state: AiReviewJobType,
      action: PayloadAction<{section: K; data: Partial<ProtocolSectionMap[K]>}>
    ) {
      const {section, data} = action.payload;
      switch (section) {
        case 'caseContext':
          state.protocol.caseContext = {
            ...state.protocol.caseContext,
            ...(data as Partial<CaseContext>),
          };
          break;
        case 'relevanceCriteria':
          state.protocol.relevanceCriteria = {
            ...state.protocol.relevanceCriteria,
            ...(data as Partial<RelevanceCriteria>),
          };
          break;
        case 'issueCoding':
          state.protocol.issueCoding = {
            ...state.protocol.issueCoding,
            ...(data as Partial<IssueCoding>),
          };
          break;
      }
    },

    setProtocolStatus(state, action: PayloadAction<ProtocolStatus>) {
      state.protocol.status = action.payload;
    },

    setProtocolData(state, action: PayloadAction<Partial<ProtocolFormData>>) {
      state.protocol = {...state.protocol, ...action.payload};
    },

    setProtocolIds(
      state,
      action: PayloadAction<{protocolId: number; protocolVersionId: number}>
    ) {
      state.protocol.protocolId = action.payload.protocolId;
      state.protocol.protocolVersionId = action.payload.protocolVersionId;
      state.initialProtocol.protocolId = action.payload.protocolId;
      state.initialProtocol.protocolVersionId =
        action.payload.protocolVersionId;
    },

    //"Get Protocol All Versions" response.
    setProtocolVersions(
      state,
      action: PayloadAction<{
        protocolId: number;
        versions: ProtocolVersionSummary[];
      }>
    ) {
      state.protocolVersions = {
        protocolId: action.payload.protocolId,
        versions: action.payload.versions,
      };
    },

    //Records the current protocol as the new baseline for change-detection.
    // Call this after a successful Create/Edit save, or after loading a protocol version,
    // so the "updated" flags reset to false until the next edit.
    setInitialProtocol(state, action: PayloadAction<ProtocolFormData>) {
      const next = action.payload;
      const protocolId =
        next.protocolId ??
        state.protocol.protocolId ??
        state.initialProtocol.protocolId;
      const protocolVersionId =
        next.protocolVersionId ??
        state.protocol.protocolVersionId ??
        state.initialProtocol.protocolVersionId;
      const merged: ProtocolFormData = {
        ...next,
        ...(protocolId != null ? {protocolId} : {}),
        ...(protocolVersionId != null ? {protocolVersionId} : {}),
      };
      state.initialProtocol = merged;
      state.protocol = merged;
    },
    setProtocolVersionBaseline(
      state,
      action: PayloadAction<ProtocolFormData | null>
    ) {
      state.protocolVersionBaseline = action.payload;
    },

    /**
     * Persists current protocol edits locally with draft status.
     * Survives resetProtocol via lastSavedDraft until create API exists.
     */
    saveProtocolDraft(state) {
      const draft: ProtocolFormData = {
        ...state.protocol,
        status: 'draft',
      };
      state.protocol = draft;
      state.initialProtocol = draft;
      state.lastSavedDraft = draft;
    },

    resetProtocol(state) {
      state.protocol = emptyProtocol();
      state.initialProtocol = emptyProtocol();
      state.protocolVersionBaseline = null;
      state.protocolVersions = emptyProtocolVersions();
    },

    /**
     * Clears uploaded protocol file + section content, keeps name/ids/status.
     */
    clearProtocolUploadedContent(state) {
      const {protocolName, protocolId, protocolVersionId, status} =
        state.protocol;
      state.protocol = {
        ...emptyProtocol(),
        protocolName,
        protocolId,
        protocolVersionId,
        status,
        fileDetails: undefined,
      };
    },

    // -------------------------------------------------------------------

    showToast(
      state,
      action: PayloadAction<{
        title: string;
        message: string;
        status: ToastType[];
      }>
    ) {
      state.toast = {
        open: true,
        title: action.payload.title,
        message: action.payload.message,
        status: action.payload.status,
      };
    },

    hideToast(state) {
      state.toast = toastInitialState;
    },
    setEditMode(state, action: PayloadAction<Partial<EditModeState>>) {
      state.editMode = {...state.editMode, ...action.payload};
    },
    resetEditMode(state) {
      state.editMode = editModeInitialState;
    },
    setDefineCriteria(state, action: PayloadAction<string>) {
      const prev = state.defineCriteria;
      const next = action.payload;

      // Reset ONLY when switching to SAVED
      if (next === 'saved' && prev !== 'saved') {
        state.editMode = editModeInitialState;
      }

      state.defineCriteria = next;
    },

    /**
     * AVR-23583: inactive associated protocol on edit/clone.
     * Keep section content; drop the selected protocol association.
     */
    convertAppliedProtocolToManual(state) {
      const nextFormData: JobDetailFormData = {
        ...state.formData,
        isManual: true,
        reviewProtocolVersionID: null,
        parentProtocolVersionID: null,
        parentProtocolID: null,
        isProtocolModified: false,
      };
      const nextProtocol: ProtocolFormData = {
        ...state.protocol,
        protocolId: undefined,
        protocolVersionId: undefined,
        protocolName: '',
        protocolVersionName: undefined,
      };

      state.formData = nextFormData;
      state.initialFormData = nextFormData;
      state.protocol = nextProtocol;
      state.initialProtocol = nextProtocol;
      state.protocolVersionBaseline = null;
      state.defineCriteria = 'manual';
    },
  },
});

// Export actions
export const {
  setJobType,
  setFormData,
  updateJobSection,
  resetFormData,
  initializeJobCreateProtocol,
  syncProtocolToFormData,
  setInitialFormData,
  setCloneJobName,
  setProtocolName,
  initializeNewProtocol,
  updateProtocolSection,
  setProtocolStatus,
  setProtocolData,
  setProtocolIds,
  setProtocolVersions,
  setInitialProtocol,
  setProtocolVersionBaseline,
  saveProtocolDraft,
  resetProtocol,
  clearProtocolUploadedContent,
  showToast,
  hideToast,
  setEditMode,
  resetEditMode,
  setDefineCriteria,
  convertAppliedProtocolToManual,
} = AiReviewSliceV2.actions;

export default AiReviewSliceV2.reducer;
